import { Calculator, Loader2 } from "lucide-react";
import { useState, useCallback } from "react";

import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PaymentsQuotesService } from "../../dispersions/services/paymentsQuotes.service";
import { DEFAULT_COMMISSION } from "../constants/quoter.constants";
import { shippingQuotesService } from "../services/shippingQuotes.service";
import type { QuoterQuoteData } from "../types/quoter.types";
import { buildShippingQuotePayload } from "../utils/buildShippingQuotePayload.utils";
import type { CurrencyOption } from "../utils/currencyOptions.utils";
import { parseAmountInput } from "../utils/formatQuoterAmount.utils";
import { computeQuoteFromCommission } from "../utils/quoteCommission.utils";
import { QuoterHeader } from "./QuoterHeader";
import { QuoterQuotationResult } from "./QuoterQuotationResult";
import { QuoterStep1, type QuoterMode } from "./QuoterStep1";
import { QuoterStep2 } from "./QuoterStep2";
import { QuoterSummary } from "./QuoterSummary";

/**
 * Mid rate for the pair. The quoter only needs a rate — it computes the commission client-side — so it reads
 * the native rates layer instead of creating a persisted quote on every click. `GET /internal/payments/rates`
 * requires `payments:read`; a 401/403 surfaces as the form error (the legacy PHP fallback is gone with the
 * PHP integration), so the fix is granting the scope, not routing around it.
 */
async function fetchMidRate(originCurrency: string, destinationCurrency: string): Promise<number> {
  const { data } = await PaymentsQuotesService.getRate(originCurrency, destinationCurrency);
  return Number(data.rate);
}

export type QuoterFormDisplayConfig = {
  hideCommissionLine?: boolean;
  hideConvertedRow?: boolean;
  showEffectiveRate?: boolean;
  hideExchangeRateCard?: boolean;
};

type QuoterFormProps = {
  currencyOptions: CurrencyOption[];
  isLoading?: boolean;
  displayConfig?: QuoterFormDisplayConfig;
  headerTitleSuffix?: string;
};

export const QuoterForm = ({
  currencyOptions,
  isLoading = false,
  displayConfig = {},
  headerTitleSuffix,
}: QuoterFormProps) => {
  const {
    hideCommissionLine = false,
    hideConvertedRow = false,
    showEffectiveRate = false,
    hideExchangeRateCard = false,
  } = displayConfig;
  const { t } = useTranslations("quoter");
  const [mode, setMode] = useState<QuoterMode>("send");
  const [originCurrency, setOriginCurrency] = useState<CurrencyOption | null>(
    currencyOptions.find((o) => o.value === "COP") ?? currencyOptions[0] ?? null,
  );
  const [destinationCurrency, setDestinationCurrency] = useState<CurrencyOption | null>(
    currencyOptions.find((o) => o.value === "USD") ?? currencyOptions[1] ?? null,
  );
  const [amount, setAmount] = useState("");
  const [commission, setCommission] = useState(String(DEFAULT_COMMISSION));
  const [quoteData, setQuoteData] = useState<QuoterQuoteData | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const handleOriginChange = useCallback((opt: CurrencyOption | null) => {
    setOriginCurrency(opt);
    setQuoteData(null);
    setQuoteError(null);
  }, []);

  const handleDestinationChange = useCallback((opt: CurrencyOption | null) => {
    setDestinationCurrency(opt);
    setQuoteData(null);
    setQuoteError(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (quoteLoading || isLoading) return;
      if (!originCurrency || !destinationCurrency) {
        setQuoteError("Selecciona moneda de origen y destino.");
        return;
      }
      const amountNum = parseAmountInput(amount);
      if (amountNum <= 0) {
        setQuoteError("Ingresa una cantidad válida.");
        return;
      }
      setQuoteError(null);
      setQuoteLoading(true);
      try {
        const commissionNum = parseFloat(commission.replace(/,/g, ".").replace(/[^\d.]/g, "")) || 0;
        const isSameCurrency = originCurrency.value === destinationCurrency.value;

        let rate: number;
        let quoteCurrency: { from: string; to: string };

        if (isSameCurrency) {
          rate = 1;
          quoteCurrency = {
            from: originCurrency.value,
            to: destinationCurrency.value,
          };
        } else {
          rate = await fetchMidRate(originCurrency.value, destinationCurrency.value);
          quoteCurrency = {
            from: originCurrency.value,
            to: destinationCurrency.value,
          };
        }

        const computed = computeQuoteFromCommission(rate, amountNum, mode, commissionNum);
        const quote: QuoterQuoteData = {
          rate,
          currency: quoteCurrency,
          breakdown: computed.breakdown,
          fees: computed.fees,
        };

        setQuoteData(quote);

        try {
          const payload = buildShippingQuotePayload(quote);
          await shippingQuotesService.create(payload);
        } catch {
          // Quote is already shown; shipping quote creation failure is non-blocking.
        }
      } catch (err) {
        const message = HttpApiService.getErrorMessage(err) ?? t("form_error_quote");
        setQuoteError(message);
        setQuoteData(null);
      } finally {
        setQuoteLoading(false);
      }
    },
    [mode, originCurrency, destinationCurrency, amount, commission, quoteLoading, isLoading, t],
  );

  return (
    <form onSubmit={handleSubmit} className="max-w-5xl w-full">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-zinc-800 transition-all duration-300">
        <QuoterHeader titleSuffix={headerTitleSuffix} />

        <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <QuoterStep1
            mode={mode}
            onModeChange={setMode}
            hideExchangeRateCard={hideExchangeRateCard}
            exchangeRate={
              quoteData
                ? {
                    rate: quoteData.rate,
                    currencyFrom: quoteData.currency.from,
                    currencyTo: quoteData.currency.to,
                  }
                : null
            }
          />

          <div className="lg:col-span-7 flex flex-col gap-6">
            <QuoterStep2
              mode={mode}
              currencyOptions={currencyOptions}
              originCurrency={originCurrency}
              destinationCurrency={destinationCurrency}
              amount={amount}
              commission={commission}
              onOriginChange={handleOriginChange}
              onDestinationChange={handleDestinationChange}
              onAmountChange={setAmount}
              onCommissionChange={setCommission}
              isLoading={isLoading}
            />

            <QuoterSummary
              quote={quoteData}
              quoteError={quoteError}
              hideCommissionLine={hideCommissionLine}
              hideConvertedRow={hideConvertedRow}
            />

            {quoteData && (
              <QuoterQuotationResult
                quote={quoteData}
                hideCommission={hideCommissionLine}
                hideConverted={hideConvertedRow}
                showEffectiveRate={showEffectiveRate}
              />
            )}

            <button
              type="submit"
              disabled={isLoading || quoteLoading}
              className="w-full bg-green-500 hover:bg-emerald-600 disabled:bg-green-400 disabled:cursor-not-allowed text-white font-bold py-5 rounded-xl shadow-lg shadow-emerald-500/20 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {quoteLoading ? <Loader2 className="size-5 animate-spin" /> : <Calculator className="size-5" />}
              {quoteLoading ? t("form_button_quoting") : t("form_button_submit")}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};
